import React, { useState, useCallback, memo, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  useTheme,
  alpha,
  Paper,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FolderIcon from '@mui/icons-material/Folder';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Project } from '../models/types';

interface HorizontalProjectListProps {
  projects: Project[];
  currentProject: Project | null;
  onSelectProject: (project: Project) => void;
  onCreateProject: (name: string, description: string) => void;
  onDeleteProject: (id: string) => void;
}

// Ultra-compact Project Item component - just the name
const ProjectItem = memo(({ 
  project, 
  isActive, 
  onSelect, 
  onDelete 
}: { 
  project: Project; 
  isActive: boolean; 
  onSelect: () => void; 
  onDelete: (e: React.MouseEvent) => void; 
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };
  
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(e);
    handleCloseMenu();
  };
  
  // Color based on progress
  const dotColor = getProgressColor(project.overallProgress);
  
  return (
    <Box sx={{ position: 'relative', display: 'inline-block', mx: 0.5, my: 0.5 }}>
      <Tooltip title={`Progress: ${Math.round(project.overallProgress)}%`} arrow placement="top">
        <Chip
          label={project.name}
          onClick={onSelect}
          variant={isActive ? "filled" : "outlined"}
          color={isActive ? "primary" : "default"}
          size="small"
          sx={{ 
            height: 28,
            borderRadius: '14px',
            fontWeight: isActive ? 600 : 400,
            px: 1,
            '&:hover': {
              bgcolor: isActive 
                ? theme.palette.primary.main 
                : alpha(theme.palette.primary.light, 0.1),
            },
            '& .MuiChip-icon': {
              ml: 0.5,
              mr: -0.5,
            }
          }}
          icon={
            <Box 
              component="span" 
              sx={{ 
                width: 6, 
                height: 6, 
                borderRadius: '50%', 
                bgcolor: dotColor,
                display: 'inline-block'
              }} 
            />
          }
          deleteIcon={
            <IconButton
              onClick={handleOpenMenu}
              size="small"
              sx={{
                p: 0.3,
                '&:hover': {
                  bgcolor: alpha(theme.palette.background.paper, 0.8),
                },
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          }
          onDelete={handleOpenMenu}
        />
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={() => {
          onSelect();
          handleCloseMenu();
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Open Project</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Delete Project</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
});

const HorizontalProjectList: React.FC<HorizontalProjectListProps> = ({
  projects,
  currentProject,
  onSelectProject,
  onCreateProject,
  onDeleteProject,
}) => {
  const theme = useTheme();
  
  // State for the create project dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  
  // State for the delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);

  // Reference for the scrollable container
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  
  // Handlers for scrolling
  const handleScrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -400, behavior: 'smooth' });
    }
  };
  
  const handleScrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 400, behavior: 'smooth' });
    }
  };
  
  // Add touch/mouse-based scroll event handlers for better performance
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    let isDown = false;
    let startX: number;
    let scrollLeft: number;
    
    const handleMouseDown = (e: MouseEvent) => {
      isDown = true;
      container.style.cursor = 'grabbing';
      startX = e.pageX - container.offsetLeft;
      scrollLeft = container.scrollLeft;
      e.preventDefault();
    };
    
    const handleMouseUp = () => {
      isDown = false;
      container.style.cursor = 'grab';
    };
    
    const handleMouseLeave = () => {
      isDown = false;
      container.style.cursor = 'grab';
    };
    
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - container.offsetLeft;
      const walk = (x - startX) * 2; // Scroll speed multiplier
      container.scrollLeft = scrollLeft - walk;
    };
    
    // Add event listeners
    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mouseleave', handleMouseLeave);
    container.addEventListener('mousemove', handleMouseMove);
    
    // Clean up
    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mouseleave', handleMouseLeave);
      container.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);
  
  // Handlers for create project dialog
  const handleOpenCreateDialog = useCallback(() => {
    setCreateDialogOpen(true);
  }, []);
  
  const handleCloseCreateDialog = useCallback(() => {
    setCreateDialogOpen(false);
    // Reset form
    setNewProjectName('');
    setNewProjectDescription('');
  }, []);
  
  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setNewProjectName(e.target.value);
  }, []);
  
  const handleDescriptionChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setNewProjectDescription(e.target.value);
  }, []);
  
  const handleCreateProject = useCallback(() => {
    if (newProjectName.trim()) {
      onCreateProject(newProjectName.trim(), newProjectDescription.trim());
      handleCloseCreateDialog();
    }
  }, [newProjectName, newProjectDescription, onCreateProject, handleCloseCreateDialog]);
  
  // Handlers for delete confirmation dialog
  const handleOpenDeleteDialog = useCallback((project: Project) => {
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
  }, []);
  
  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setProjectToDelete(null);
  }, []);
  
  const handleConfirmDelete = useCallback(() => {
    if (projectToDelete) {
      onDeleteProject(projectToDelete.id);
      handleCloseDeleteDialog();
    }
  }, [projectToDelete, onDeleteProject, handleCloseDeleteDialog]);
  
  return (
    <Paper 
      sx={{ 
        width: '100%',
        position: 'relative',
        mb: 2,
        borderRadius: 2,
        boxShadow: 'none',
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
      }}
      elevation={0}
    >
      {/* Compact Header */}
      <Box 
        sx={{ 
          px: 2,
          py: 1, 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.background.paper, 0.6),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FolderIcon color="primary" fontSize="small" sx={{ mr: 0.75 }} />
          <Typography 
            variant="subtitle2" 
            component="h2"
            sx={{ 
              fontWeight: 600,
              fontSize: '0.875rem',
            }}
          >
            Projects
          </Typography>
        </Box>
        
        <Button
          startIcon={<AddIcon fontSize="small" />}
          onClick={handleOpenCreateDialog}
          size="small"
          variant="outlined"
          color="primary"
          sx={{
            borderRadius: '14px',
            py: 0.25,
            px: 1,
            minWidth: 0,
            textTransform: 'none',
            fontWeight: 400,
            fontSize: '0.75rem',
          }}
        >
          New
        </Button>
      </Box>
      
      {/* Scrollable Project List */}
      <Box sx={{ position: 'relative' }}>
        {/* Show scroll buttons only if needed */}
        {(() => {
          // Check if scroll is needed with proper null checking
          const container = scrollContainerRef.current;
          const needsScroll = container && container.scrollWidth > container.clientWidth;
          return needsScroll && (
          <>
            <IconButton
              onClick={handleScrollLeft}
              sx={{
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                bgcolor: 'background.paper',
                boxShadow: 1,
                p: 0.5,
              }}
              size="small"
            >
              <KeyboardArrowLeftIcon fontSize="small" />
            </IconButton>
            
            <IconButton
              onClick={handleScrollRight}
              sx={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                bgcolor: 'background.paper',
                boxShadow: 1,
                p: 0.5,
              }}
              size="small"
            >
              <KeyboardArrowRightIcon fontSize="small" />
            </IconButton>
          </>
        )})()}
        
        {/* Project items with optimized rendering - chip style */}
        <Box
          ref={scrollContainerRef}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            overflowX: 'auto',
            py: 1.5,
            px: 2,
            scrollbarWidth: 'none', // Firefox
            '&::-webkit-scrollbar': {
              display: 'none', // Chrome, Safari, Opera
            },
            '-ms-overflow-style': 'none', // IE, Edge
            WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
          }}
        >
          {/* Use memoized project items to prevent unnecessary re-renders */}
          {useMemo(() => 
            projects.map(project => (
              <ProjectItem
                key={project.id}
                project={project}
                isActive={currentProject?.id === project.id}
                onSelect={() => onSelectProject(project)}
                onDelete={(e) => {
                  e.stopPropagation();
                  handleOpenDeleteDialog(project);
                }}
              />
            )),
            [projects, currentProject?.id] // Only re-render when these dependencies change
          )}
          
          {/* Add project button - ultra simplified */}
          <Chip
            icon={<AddIcon fontSize="small" />}
            label="New Project"
            onClick={handleOpenCreateDialog}
            variant="outlined"
            color="primary"
            size="small"
            sx={{ 
              height: 28,
              borderRadius: '14px',
              mx: 0.5,
              my: 0.5,
              borderStyle: 'dashed',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.05),
              }
            }}
          />
        </Box>
      </Box>
      
      {/* Create Project Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={handleCloseCreateDialog}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: theme.shadows[10],
          }
        }}
      >
        <DialogTitle>Create New Project</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter the details for your new requirements project.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Project Name"
            type="text"
            fullWidth
            value={newProjectName}
            onChange={handleNameChange}
            variant="outlined"
            required
          />
          <TextField
            margin="dense"
            id="description"
            label="Description"
            type="text"
            fullWidth
            multiline
            rows={3}
            value={newProjectDescription}
            onChange={handleDescriptionChange}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCloseCreateDialog}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateProject} 
            variant="contained" 
            disabled={!newProjectName.trim()}
            sx={{
              borderRadius: 2,
              px: 3,
            }}
            startIcon={<AddIcon />}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={handleCloseDeleteDialog}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: theme.shadows[10],
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
          }
        }}
      >
        <DialogTitle>Delete Project</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{projectToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCloseDeleteDialog}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
            sx={{
              borderRadius: 2,
              px: 3,
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

// Helper functions
function getProgressColor(progress: number): string {
  if (progress < 25) return '#ef4444';  // red
  if (progress < 50) return '#f59e0b';  // amber
  if (progress < 75) return '#3b82f6';  // blue
  return '#10b981';  // green
}

export default HorizontalProjectList;
